import React from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * The site's markdown: runtime specs and developments render through this, so
 * they read the same. `artifacts` maps `artifact:<name>` links and images to the
 * entry's own files (figures, data) stored beside it.
 */
const COMPONENTS = {
  h1: ({ node, ...p }) => <h1 className="text-3xl font-normal text-white mt-8 mb-2" {...p} />,
  h2: ({ node, ...p }) => <h2 className="text-2xl font-normal text-white mt-10 mb-2 pb-2 border-b border-[#22281f]" {...p} />,
  h3: ({ node, ...p }) => <h3 className="text-xl font-normal text-white mt-6 mb-1" {...p} />,
  h4: ({ node, ...p }) => <h4 className="text-lg font-normal text-white mt-4 mb-1" {...p} />,
  p:  ({ node, ...p }) => <p  className="text-[#cccccc] leading-relaxed" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 text-[#cccccc] leading-relaxed space-y-1 marker:text-[#555555]" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 text-[#cccccc] leading-relaxed space-y-1 marker:text-[#555555]" {...p} />,
  li: ({ node, ...p }) => <li {...p} />,
  a:  ({ node, ...p }) => <a  className="text-white underline decoration-[#3a413a] underline-offset-4 hover:decoration-white" {...p} />,
  strong: ({ node, ...p }) => <strong className="text-white font-normal" {...p} />,
  em: ({ node, ...p }) => <em className="text-white italic" {...p} />,
  hr: () => <hr className="border-[#22281f] my-8" />,
  blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-[#3a413a] pl-4 text-[#aaaaaa] italic" {...p} />,
  code: ({ inline, className, children, ...p }) => (
    inline
      ? <code className="bg-[#141a15] text-[#dddddd] px-1.5 py-0.5 rounded text-sm font-mono normal-case" {...p}>{children}</code>
      : <code className={`font-mono normal-case ${className || ''}`} {...p}>{children}</code>
  ),
  pre: ({ node, ...p }) => <pre className="bg-[#0a0e0b] border border-[#1a201b] rounded p-4 overflow-x-auto text-xs sm:text-sm normal-case" {...p} />,
  table: ({ node, ...p }) => <div className="overflow-x-auto"><table className="text-sm border border-[#22281f] w-full" {...p} /></div>,
  th: ({ node, ...p }) => <th className="border border-[#22281f] px-3 py-2 text-left text-white font-normal bg-white/[0.02]" {...p} />,
  td: ({ node, ...p }) => <td className="border border-[#22281f] px-3 py-2 text-[#cccccc] tabular-nums" {...p} />,
  // markdown puts images inside paragraphs, so the caption is a span, not a figcaption
  img: ({ node, alt, ...p }) => (
    <span className="block my-6">
      <img alt={alt} className="w-full border border-[#1e251f] rounded-lg bg-[#0a0e0b]" loading="lazy" {...p} />
      {alt && <span className="block text-xs text-[#777777] mt-2 font-mono">{alt}</span>}
    </span>
  ),
};

export default function Markdown({ children, artifacts }) {
  const urlTransform = (url) => {
    if (artifacts && url.startsWith('artifact:')) {
      return artifacts[url.slice('artifact:'.length)] || '';
    }
    return defaultUrlTransform(url);
  };
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS} urlTransform={urlTransform}>
      {children}
    </ReactMarkdown>
  );
}
